import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HistoryIcon from '@mui/icons-material/History';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from '@mui/material';
import { useCallback, useMemo, useState, type MouseEvent } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { ProjectChatRowMenu } from './ProjectChatRowMenu';
import { useInternalChatVisibilityEnabled } from '../hooks/useInternalChatVisibilityEnabled';
import { clearStickyActiveChat } from '../hooks/useStickyActiveChat';
import type { ChatSession } from '../lib/api/types';
import { useProjectChatsQuery } from '../lib/api/useProjectChats';

interface ActiveChatPickerProps {
  projectId: string;
  /** The currently-active chat id (from `?chat=<id>`), or `undefined` in the
   * welcome/empty state — no row is marked active and the trigger shows the
   * "My chats" label. */
  activeChatId: string | undefined;
  /** Display label for the trigger — the current chat's title, already
   * resolved by the parent (`ActiveChatHeader`) including its 'New chat' /
   * 'My chats' fallbacks. Plumbed in rather than re-derived to keep one
   * source of truth for the title fallback rule. */
  triggerLabel: string;
  /** When the parent is still loading the active chat record. Renders a
   * small spinner instead of the trigger label so the user doesn't see
   * 'My chats' flash before the real title arrives. */
  isParentLoading: boolean;
  /** "+ New chat" action. Drops to the ephemeral welcome shell (no eager
   * row) — the parent owns the sticky-clear + drop-`?chat=` behavior. */
  onNewChat: () => void;
}

const sortByLastActiveDesc = (chats: ChatSession[]): ChatSession[] =>
  [...chats].sort((a, b) => Date.parse(b.lastActiveAt) - Date.parse(a.lastActiveAt));

const titleFor = (chat: ChatSession): string => chat.title?.trim() || 'New chat';

// Absolute "May 24, 2026 | 10:42 AM" — matches the design (80:12368),
// which shows absolute timestamps under each chat title rather than the
// relative "3h ago" used in the legacy chats list. Two formatters (not one
// + part-filtering): the time separator is a position-dependent literal, so
// splitting `formatToParts` by type would drop the colon.
const dateFormatter = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
});
const timeFormatter = new Intl.DateTimeFormat('en-US', {
  hour: 'numeric',
  minute: '2-digit',
});

const formatTimestamp = (iso: string): string => {
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return '';
  const date = new Date(ts);
  return `${dateFormatter.format(date)} | ${timeFormatter.format(date)}`;
};

/**
 * Single consolidated chat switcher (OR-6123 surface E / design 80:12368).
 *
 * The trigger is a flex-fill ``Button`` (history clock icon + current chat
 * title + caret) styled to behave like the previous title ``Typography``:
 * it consumes the remaining row width AND ellipsis-truncates a long title.
 *
 * The dropdown is ONE flat list of the user's own chats — no "Team chats"
 * section, no share/lock affordance (D3/D5: owner-only, no sharing). At the
 * top sits a "+ New chat" action wired to the ephemeral welcome-shell flow;
 * the active chat is marked with a check; each row carries an absolute
 * timestamp. Each row's rename/delete kebab ({@link ProjectChatRowMenu},
 * reused from the legacy chats list) is hover-only — it's this dropdown's
 * only rename/delete affordance now that the header kebab is gone. Deleting
 * the currently-open chat's own row drops `?chat=` and clears sticky so the
 * panel falls back to the welcome shell.
 *
 * The trigger label is muted (`text.secondary`) when it's showing the
 * welcome-state 'My chats' placeholder AND the project has no chats yet —
 * once a chat exists, 'My chats' is a real entry point and reads at full
 * contrast.
 *
 * Presentational + self-contained: it queries only the ``me`` chats list and
 * takes its navigation / new-chat callbacks as props, so it can be dropped
 * into any chat header without rebuilding.
 *
 * TODO: cleanup with research_assistant_gen_3 ff
 */
export const ActiveChatPicker = ({
  projectId,
  activeChatId,
  triggerLabel,
  isParentLoading,
  onNewChat,
}: ActiveChatPickerProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const meQuery = useProjectChatsQuery({ projectId, ownerFilter: 'me' });

  // OR-6455: a second "All" tab, internal-viewer-only. Hidden entirely (no
  // tab strip at all) for every other caller — this dropdown stays exactly
  // the "ONE flat list of the user's own chats" it was before for everyone
  // else. `enabled` is additionally gated on the tab actually being open.
  //
  // This query key (`chatsForProject(projectId, 'all')`) is shared with
  // `useStickyActiveChat` and `useActiveChat` — TanStack Query fires a
  // fetch for a key the moment ANY observer of it has `enabled: true`, so
  // "never clicking All" only means THIS caller never issues it; either of
  // the other two can still fire it independently (each has its own
  // narrower gate — see their docstrings) when the URL carries a chat id
  // this user doesn't already own.
  const internalVisibilityEnabled = useInternalChatVisibilityEnabled();
  const [activeTab, setActiveTab] = useState<'mine' | 'all'>('mine');
  const allQuery = useProjectChatsQuery({
    projectId,
    ownerFilter: 'all',
    enabled: internalVisibilityEnabled && activeTab === 'all',
  });

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleOpen = useCallback((event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleSelect = useCallback(
    (chatId: string) => {
      // Click on the already-active row → just close. Re-navigating
      // would trigger a redundant route update + chat remount.
      if (chatId === activeChatId) {
        setAnchorEl(null);
        return;
      }
      setAnchorEl(null);
      navigate({ pathname: location.pathname, search: `?chat=${chatId}` });
    },
    [activeChatId, location.pathname, navigate]
  );

  const handleNewChatClick = useCallback(() => {
    setAnchorEl(null);
    onNewChat();
  }, [onNewChat]);

  // Deleting the OPEN chat's own row leaves nothing to view — drop `?chat=`
  // and clear sticky so the panel falls back to the welcome shell instead
  // of pointing at a now-404'd chat. Deleting any other row is a no-op here
  // — the list re-renders from the invalidated cache on its own.
  const handleRowDeleted = useCallback(
    (deletedChatId: string) => {
      setAnchorEl(null);
      if (deletedChatId !== activeChatId) return;
      clearStickyActiveChat(projectId);
      const next = new URLSearchParams(searchParams);
      next.delete('chat');
      const nextSearch = next.toString();
      navigate({ pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : '' });
    },
    [activeChatId, projectId, searchParams, navigate, location.pathname]
  );

  const myChats = useMemo(() => sortByLastActiveDesc(meQuery.data?.data ?? []), [meQuery.data]);
  // Jira OR-6455 comment #81601, item 4: the "Other chats" tab should show
  // only chats the caller doesn't already own — `owner=all` (allQuery)
  // returns every chat in the project INCLUDING the caller's own, so those
  // are filtered out here rather than changing what the BE returns.
  // Comparing against `myChats`' own ids (not a separate current-user-id
  // lookup) — that list IS exactly "the chats this caller owns", already
  // fetched for the other tab.
  const myChatIds = useMemo(() => new Set(myChats.map((chat) => chat.id)), [myChats]);
  const otherChats = useMemo(
    () => sortByLastActiveDesc(allQuery.data?.data ?? []).filter((chat) => !myChatIds.has(chat.id)),
    [allQuery.data, myChatIds]
  );

  const showingAll = internalVisibilityEnabled && activeTab === 'all';
  const isLoading = showingAll ? allQuery.isLoading : meQuery.isLoading;
  const isError = showingAll ? allQuery.isError : meQuery.isError;

  // The trigger reads 'My chats' only in the welcome state (no active
  // chat); mute it while the project genuinely has nothing to switch to,
  // so it reads as a placeholder rather than an already-populated entry
  // point. Defaults to muted before the list resolves — a brand-new
  // project is the common case, and this corrects itself the instant the
  // (typically cache-warm) query settles.
  //
  // OR-6455: an internal+flagged caller with zero chats of their own still
  // has the "All (internal)" tab to browse — the trigger must stay open
  // for them even when `myChats` is empty, or they can never reach it.
  // Every other caller is unaffected: `internalVisibilityEnabled` is
  // false, so this collapses to the original `myChats.length === 0` check.
  const muted = !activeChatId && myChats.length === 0 && !internalVisibilityEnabled;

  return (
    <>
      <Button
        size="small"
        onClick={handleOpen}
        // Nothing to switch to yet — the trigger is inert, not just
        // unlabeled. `muted` is only ever true with no active chat (the
        // welcome shell is already showing), so disabling loses no
        // reachable action — the dropdown's "+ New chat" would just
        // reopen the screen the user is already on.
        disabled={muted}
        startIcon={<HistoryIcon fontSize="inherit" />}
        // No dropdown affordance when there's nothing to switch to — the
        // caret implies a browsable history, which doesn't exist yet.
        endIcon={muted ? undefined : <ExpandMoreIcon fontSize="inherit" />}
        data-testid="active-chat-picker-trigger"
        aria-haspopup="menu"
        aria-expanded={anchorEl ? 'true' : 'false'}
        sx={{
          // Content-sized, not flex-filled — the caret sits right after the
          // title (mockup), not pinned to the far right of the header row.
          // `maxWidth` + the title's own `minWidth: 0`/ellipsis still let a
          // genuinely long title truncate instead of overflowing the row.
          maxWidth: '100%',
          minWidth: 0,
          justifyContent: 'flex-start',
          textTransform: 'none',
          padding: theme.spacing(0.5, 1),
          overflow: 'hidden',
          color: muted ? 'text.secondary' : 'text.primary',
          // No effect on the rendered label — the child Typography's own
          // explicit variant color/weight always wins (see below). Left off
          // here entirely rather than kept as a dead override.
          '& .MuiButton-startIcon': {
            marginRight: theme.spacing(1),
            flexShrink: 0,
            color: 'text.secondary',
          },
          '& .MuiButton-endIcon': {
            marginLeft: theme.spacing(0.5),
            flexShrink: 0,
          },
        }}
      >
        {isParentLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CircularProgress size={14} />
          </Box>
        ) : (
          <Typography
            // 14px/medium (VDS spec: chat menu typography — the trigger and
            // the dropdown's chat titles/dates all sit at 14px for visual
            // consistency with each other and the surrounding RA panel
            // content; a prior 16px here was inconsistent with that). No
            // manual `fontWeight` override needed — subtitle2 already is medium.
            variant="subtitle2"
            noWrap
            component="span"
            sx={{
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              textAlign: 'left',
              // The VDS typography variants set an explicit color
              // (neutral-900) per theme config, which always wins over an
              // ancestor's inherited `sx.color` — so the muted state has to
              // be set here directly, not just on the parent Button.
              color: muted ? 'text.secondary' : 'text.primary',
            }}
            data-testid="active-chat-title"
          >
            {triggerLabel}
          </Typography>
        )}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        data-testid="active-chat-picker-menu"
        // Figma spec (80:12368): fixed 380px width, 4px vertical list padding.
        slotProps={{ paper: { sx: { width: 380 } } }}
        MenuListProps={{ sx: { paddingY: theme.spacing(1) } }}
      >
        <MenuItem
          onClick={handleNewChatClick}
          data-testid="active-chat-picker-new-chat"
          sx={{ color: 'primary.main' }}
        >
          <ListItemIcon sx={{ color: 'primary.main' }}>
            <AddIcon fontSize="small" />
          </ListItemIcon>
          {/* ListItemText renders its label via Typography, whose VDS variant
              styles set an explicit (dark) color that wins over the MenuItem's
              inherited `sx.color` — same override issue as the trigger label,
              so the blue has to be set here directly too. `subtitle2` is the
              VDS variant for 14px/medium — matches the row titles below with
              no manual `fontWeight` override needed. */}
          <ListItemText primaryTypographyProps={{ variant: 'subtitle2', color: 'primary.main' }}>
            New chat
          </ListItemText>
        </MenuItem>
        <Divider />
        {internalVisibilityEnabled && (
          // OR-6455: hidden entirely for every non-internal / non-flagged
          // caller — everyone else keeps the exact single-list dropdown
          // this component had before. Switching tabs never navigates;
          // it only changes which cached partition the list below reads.
          <Tabs
            value={activeTab}
            onChange={(_e, value: 'mine' | 'all') => setActiveTab(value)}
            data-testid="active-chat-picker-tabs"
            sx={{ minHeight: 0, paddingX: 2, marginBottom: 1 }}
          >
            {/* MUI's default Tab style force-uppercases the label
                regardless of the string's own casing — without this
                override "My chats"/"Other chats" would still render as
                "MY CHATS"/"OTHER CHATS" despite being sentence-cased in
                code (Jira OR-6455 comment #81601, item 4). */}
            <Tab
              value="mine"
              label="My chats"
              data-testid="active-chat-picker-tab-mine"
              sx={{ minHeight: 0, paddingY: 1, textTransform: 'none' }}
            />
            <Tab
              value="all"
              label="Other chats"
              data-testid="active-chat-picker-tab-all"
              sx={{ minHeight: 0, paddingY: 1, textTransform: 'none' }}
            />
          </Tabs>
        )}
        {isLoading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              padding: theme.spacing(1.5),
            }}
            data-testid="active-chat-picker-loading"
          >
            <CircularProgress size={16} />
          </Box>
        ) : isError ? (
          <Box sx={{ padding: theme.spacing(1.5, 2) }}>
            <Typography variant="body2" color="error" data-testid="active-chat-picker-error">
              Couldn’t load chats.
            </Typography>
          </Box>
        ) : (showingAll ? otherChats : myChats).length === 0 ? (
          <Box sx={{ padding: theme.spacing(0.5, 2) }} data-testid="active-chat-picker-me-empty">
            <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
              {showingAll ? 'No other chats in this project yet' : 'No chats yet'}
            </Typography>
          </Box>
        ) : (
          (showingAll ? otherChats : myChats).map((chat) => {
            const isActive = chat.id === activeChatId;
            return (
              <MenuItem
                key={chat.id}
                selected={isActive}
                onClick={() => handleSelect(chat.id)}
                data-testid={`active-chat-picker-row-${chat.id}`}
                sx={{ alignItems: 'flex-start', paddingY: theme.spacing(1) }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    minWidth: 0,
                    flex: 1,
                    flexBasis: 0,
                  }}
                >
                  {/* subtitle2 = 14px/medium (VDS) — the token-correct variant
                      for this weight, no manual `fontWeight` override needed. */}
                  <Typography variant="subtitle2" noWrap sx={{ minWidth: 0 }}>
                    {titleFor(chat)}
                  </Typography>
                  {/* OR-6455: this row isn't the caller's own — label it
                      clearly BEFORE the click opens it, not only after (a
                      read-only banner inside the opened chat isn't enough
                      on its own). `ownerDisplayName` is a best-effort BE
                      Auth0 lookup (batched once per list call); falls back
                      to the raw id on a lookup miss. Icon + warning color
                      echo the read-only banner (ReadOnlyChatPanel) so the
                      same visual cue means the same thing everywhere; the
                      text itself is `text.primary` per Jira OR-6455
                      comment #81601, item 5. */}
                  {showingAll && (
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={0.5}
                      sx={{ minWidth: 0 }}
                      data-testid={`active-chat-picker-row-owner-${chat.id}`}
                    >
                      <VisibilityIcon sx={{ fontSize: 14, color: 'warning.main', flexShrink: 0 }} />
                      <Typography
                        variant="caption"
                        noWrap
                        sx={{ minWidth: 0, color: 'text.primary' }}
                      >
                        Owner: {chat.ownerDisplayName ?? chat.ownerUserId}
                      </Typography>
                    </Stack>
                  )}
                  {/* body2 = 14px/regular (VDS) — the date sits at the same
                      14px as the title per spec (chat menu typography);
                      caption (12px) was too small. */}
                  <Typography variant="body2" noWrap sx={{ minWidth: 0, color: 'text.secondary' }}>
                    {formatTimestamp(chat.lastActiveAt)}
                  </Typography>
                </Box>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0.5}
                  sx={{ flexShrink: 0, marginLeft: theme.spacing(1) }}
                >
                  {isActive && (
                    <CheckCircleIcon
                      fontSize="small"
                      color="success"
                      data-testid={`active-chat-picker-active-check-${chat.id}`}
                    />
                  )}
                  {/* Rename/delete is an owner-only affordance — never shown
                      on an "All" row, both because it isn't the caller's
                      chat and because this whole tab is read-only. */}
                  {!showingAll && (
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        // Hover-only via opacity, NOT `display: none` — the
                        // kebab IconButton is the anchor for its own Rename/
                        // Delete popup. Opening that popup moves the pointer
                        // off this row, dropping its `:hover` state; if the
                        // anchor were `display: none` at that point, its
                        // collapsed (zero-size) box would send the popup's
                        // position calculation to (0, 0) — a real bug this
                        // reuse hit. Opacity keeps the layout box (and its
                        // position) valid regardless of hover state.
                        opacity: 0,
                        pointerEvents: 'none',
                        '.MuiMenuItem-root:hover &, &:focus-within': {
                          opacity: 1,
                          pointerEvents: 'auto',
                        },
                      }}
                    >
                      <ProjectChatRowMenu chat={chat} onDeleted={() => handleRowDeleted(chat.id)} />
                    </Box>
                  )}
                </Stack>
              </MenuItem>
            );
          })
        )}
      </Menu>
    </>
  );
};
